// src/components/admin/SGCPromoteDraftsPanel.jsx
//
// Drop-in panel for the SuperAdmin page that lets you bulk-promote any
// stopGoContinueDrafts that never got submitted into the real
// stopGoContinue collection. Originals are kept in place; each promoted
// submission is tagged with promotedFromDraft: true.
//
// Calls the promoteSGCDrafts Cloud Function (onRequest, bearer token).

import React, { useState, useEffect, useCallback } from 'react';
import { db, auth } from '../../config/firebase';
import { collection, onSnapshot } from 'firebase/firestore';

const FUNCTION_URL =
  'https://us-central1-kings-app-74847.cloudfunctions.net/promoteSGCDrafts';

const KA = {
  red:   '#E83530',
  navy:  '#233C5F',
  gold:  '#A29164',
  paper: '#FAF8F4',
  ink:   '#1A1A1A',
  mute:  '#6B6B6B',
  line:  '#E5E1D8',
};

export default function SGCPromoteDraftsPanel() {
  const [drafts, setDrafts] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);

  // Subscribe to drafts.
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'stopGoContinueDrafts'),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setDrafts(list);
        setLoaded(true);
      },
      (err) => {
        console.error('drafts listener error', err);
        setError(err.message || 'Failed to load drafts.');
        setLoaded(true);
      }
    );
    return () => unsub();
  }, []);

  // Subscribe to existing submissions so we can compute who's already in.
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'stopGoContinue'),
      (snap) => setSubmissions(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (err) => console.error('subs listener error', err)
    );
    return () => unsub();
  }, []);

  const submittedScribeUids = new Set(
    submissions.map((s) => s.scribeUid).filter(Boolean)
  );

  // Decorate each draft with promote-eligibility.
  const decorated = drafts.map((d) => ({
    ...d,
    alreadySubmitted: submittedScribeUids.has(d.id),
  }));

  const eligibleCount = decorated.filter((d) => !d.alreadySubmitted).length;

  const promote = useCallback(async () => {
    setError('');
    setResult(null);
    setRunning(true);
    setConfirming(false);
    try {
      const idToken = await auth.currentUser?.getIdToken();
      if (!idToken) throw new Error('Not signed in.');
      const resp = await fetch(FUNCTION_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({}),
      });
      const body = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(body.error || `HTTP ${resp.status}`);
      }
      setResult(body);
    } catch (e) {
      console.error('Promote failed', e);
      setError(e.message || 'Promotion failed.');
    } finally {
      setRunning(false);
    }
  }, []);

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div>
          <h3 style={styles.h3}>Stop / Go / Continue · Draft Promotion</h3>
          <div style={styles.sub}>
            Bulk-submit any incomplete drafts on behalf of their scribes.
            Originals are kept in <code>stopGoContinueDrafts</code>.
          </div>
        </div>
        <div style={styles.stats}>
          <Stat label="Total drafts" value={loaded ? drafts.length : '—'} />
          <Stat label="Eligible" value={loaded ? eligibleCount : '—'} color={KA.red} />
        </div>
      </div>

      {!loaded && (
        <div style={styles.muted}>Loading drafts…</div>
      )}

      {loaded && drafts.length === 0 && (
        <div style={styles.muted}>No drafts in the collection.</div>
      )}

      {loaded && drafts.length > 0 && (
        <div style={styles.tableWrap}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Scribe</th>
                <th style={styles.th}>Email</th>
                <th style={styles.th}>Table</th>
                <th style={styles.th}>Status</th>
              </tr>
            </thead>
            <tbody>
              {decorated.map((d) => (
                <tr key={d.id}>
                  <td style={styles.td}>{d.scribeName || '—'}</td>
                  <td style={styles.td}>{d.scribeEmail || '—'}</td>
                  <td style={styles.td}>{d.tableNumber || '(none)'}</td>
                  <td style={styles.td}>
                    {d.alreadySubmitted ? (
                      <span style={styles.pillMuted}>Already submitted</span>
                    ) : (
                      <span style={styles.pillEligible}>Will be promoted</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {error && <div style={styles.error}>{error}</div>}

      {result && (
        <div style={styles.success}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Done.</div>
          Promoted <b>{result.promoted}</b>
          {result.promoted === 1 ? ' draft' : ' drafts'} ·
          skipped <b>{result.skipped}</b>
          {result.skipped === 1 ? ' draft' : ' drafts'}
          {' '}out of <b>{result.drafts}</b> total.
        </div>
      )}

      <div style={styles.actions}>
        {!confirming ? (
          <button
            type="button"
            style={{
              ...styles.btn,
              background: eligibleCount === 0 || running ? KA.mute : KA.red,
              cursor: eligibleCount === 0 || running ? 'not-allowed' : 'pointer',
            }}
            disabled={eligibleCount === 0 || running}
            onClick={() => setConfirming(true)}
          >
            {running
              ? 'Working…'
              : eligibleCount === 0
                ? 'Nothing to promote'
                : `Promote ${eligibleCount} draft${eligibleCount === 1 ? '' : 's'}`}
          </button>
        ) : (
          <div style={styles.confirmBox}>
            <div style={{ marginBottom: 8 }}>
              This will create {eligibleCount}{' '}
              new {eligibleCount === 1 ? 'submission' : 'submissions'} in{' '}
              <code>stopGoContinue</code>. Originals will be kept. Proceed?
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                type="button"
                style={{ ...styles.btn, background: KA.red }}
                onClick={promote}
              >
                Yes, promote
              </button>
              <button
                type="button"
                style={{ ...styles.btn, background: 'white', color: KA.navy, border: `1px solid ${KA.navy}` }}
                onClick={() => setConfirming(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={styles.stat}>
      <div style={{ ...styles.statValue, color: color || KA.navy }}>{value}</div>
      <div style={styles.statLabel}>{label}</div>
    </div>
  );
}

const styles = {
  card: {
    background: 'white',
    border: `1px solid ${KA.line}`,
    borderRadius: 10,
    padding: 20,
    marginBottom: 16,
    fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
    color: KA.ink,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    gap: 16,
    marginBottom: 14,
  },
  h3: {
    fontFamily: 'Georgia, serif',
    color: KA.navy,
    margin: '0 0 4px',
    fontSize: 18,
  },
  sub: {
    color: KA.mute,
    fontSize: 13,
    maxWidth: 480,
    lineHeight: 1.4,
  },
  stats: {
    display: 'flex',
    gap: 12,
  },
  stat: {
    background: KA.paper,
    border: `1px solid ${KA.line}`,
    borderRadius: 8,
    padding: '6px 12px',
    minWidth: 80,
    textAlign: 'center',
  },
  statValue: {
    fontFamily: 'Georgia, serif',
    fontSize: 22,
    fontWeight: 700,
    lineHeight: 1,
  },
  statLabel: {
    fontSize: 10,
    color: KA.mute,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    marginTop: 4,
  },
  muted: {
    color: KA.mute,
    fontSize: 13,
    padding: '12px 0',
  },
  tableWrap: {
    border: `1px solid ${KA.line}`,
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 14,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 13,
  },
  th: {
    textAlign: 'left',
    padding: '8px 10px',
    background: KA.paper,
    borderBottom: `1px solid ${KA.line}`,
    color: KA.mute,
    fontWeight: 600,
    textTransform: 'uppercase',
    fontSize: 11,
    letterSpacing: 0.5,
  },
  td: {
    padding: '8px 10px',
    borderBottom: `1px solid ${KA.line}`,
  },
  pillEligible: {
    background: '#E8F5E9',
    color: '#2E7D32',
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
  },
  pillMuted: {
    background: KA.line,
    color: KA.mute,
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 11,
    fontWeight: 600,
  },
  error: {
    background: '#FFEBEE',
    color: KA.red,
    border: `1px solid ${KA.red}`,
    padding: '10px 14px',
    borderRadius: 6,
    fontSize: 13,
    marginBottom: 12,
  },
  success: {
    background: '#E8F5E9',
    color: '#2E7D32',
    border: '1px solid #2E7D32',
    padding: '10px 14px',
    borderRadius: 6,
    fontSize: 13,
    marginBottom: 12,
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  btn: {
    padding: '9px 18px',
    border: 'none',
    borderRadius: 6,
    color: 'white',
    fontSize: 13,
    fontWeight: 700,
    fontFamily: 'inherit',
  },
  confirmBox: {
    background: '#FFF8EC',
    border: `1px solid ${KA.gold}`,
    padding: 14,
    borderRadius: 8,
    fontSize: 13,
    color: KA.ink,
    width: '100%',
  },
};